import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import type {
  SciAnalysis, SciAnalysisRow, SciContact, SciDecision, SciManualTreatment,
} from '../../../@types/SciContactImport';
import { formatOfficer, SCI_LEVEL_LABELS } from '../../../@types/SciContactImport';
import { ROUTES } from '../../../utils/routes';
import Badge from '../../common/Badge';

type Filter = 'match' | 'no_match' | 'not_importable';

interface SciImportReviewProps {
  analysis: SciAnalysis;
  manualTreatment: SciManualTreatment | null;
  onSave: (manualTreatment: SciManualTreatment) => Promise<void>;
  saving: boolean;
  onApply: () => Promise<void>;
  applying: boolean;
  hasBeenApplied: boolean;
  appliedImportId: number | null;
  isLatestTestImport: boolean;
}

const isReviewable = (row: SciAnalysisRow): boolean => row.status === 'match' || row.status === 'no_match';

const defaultDecision = (row: SciAnalysisRow): SciDecision => ({
  accepted: row.status === 'match' && row.level === 'sure',
  officerKey: row.proposedOfficerKey,
  email: row.proposedEmail,
  replaceExisting: false,
});

const formatContact = (contact: SciContact): string => [
  [contact.civilite, contact.nom, contact.prenom].filter(Boolean).join(' '),
  contact.email,
  contact.telephone,
].filter(Boolean).join(' · ');

const levelBadgeMode = (row: SciAnalysisRow): 'success' | 'warning' | 'error' => {
  if (row.level === 'sure') {return 'success';}
  if (row.level === 'probable') {return 'warning';}
  return 'error';
};

const SciImportReview: React.FC<SciImportReviewProps> = ({
  analysis,
  manualTreatment,
  onSave,
  saving,
  onApply,
  applying,
  hasBeenApplied,
  appliedImportId,
  isLatestTestImport,
}) => {
  const [decisions, setDecisions] = useState<Record<string, SciDecision>>(() => {
    const initial: Record<string, SciDecision> = {};
    analysis.rows.filter(isReviewable).forEach((row) => {
      initial[row.siren] = manualTreatment?.sciDecisions?.[row.siren] ?? defaultDecision(row);
    });
    return initial;
  });
  const [filter, setFilter] = useState<Filter>('match');
  const [levelFilter, setLevelFilter] = useState<'all' | 'sure' | 'probable' | 'conflict'>('all');

  const counts = useMemo(() => ({
    match: analysis.rows.filter((row) => row.status === 'match').length,
    no_match: analysis.rows.filter((row) => row.status === 'no_match').length,
    not_importable: analysis.rows.filter((row) => !isReviewable(row)).length,
    accepted: Object.values(decisions).filter((decision) => decision.accepted).length,
  }), [analysis.rows, decisions]);

  const visibleRows = useMemo(() => analysis.rows.filter((row) => {
    if (filter === 'not_importable') {return !isReviewable(row);}
    if (row.status !== filter) {return false;}
    if (filter !== 'match' || levelFilter === 'all') {return true;}
    if (levelFilter === 'conflict') {return row.existingState === 'different';}
    return row.level === levelFilter;
  }), [analysis.rows, filter, levelFilter]);

  const updateDecision = (siren: string, patch: Partial<SciDecision>) => {
    setDecisions((previous) => ({ ...previous, [siren]: { ...previous[siren], ...patch } }));
  };

  const setAllSure = (accepted: boolean) => {
    setDecisions((previous) => {
      const next = { ...previous };
      analysis.rows.forEach((row) => {
        if (row.status === 'match' && row.level === 'sure') {
          next[row.siren] = { ...next[row.siren], accepted };
        }
      });
      return next;
    });
  };

  const uncheckAll = () => {
    setDecisions((previous) => Object.fromEntries(
      Object.entries(previous).map(([siren, decision]) => [siren, { ...decision, accepted: false }]),
    ));
  };

  const handleSave = () => onSave({ status: 'in_progress', sciDecisions: decisions });

  const summary = analysis.summary;
  // "Client en gestion" answers of the file are written for every SCI of the scope, whatever the contact review
  const managedChanges = summary.managedChanges ?? 0;

  const handleApply = async() => {
    const conflictsReplaced = analysis.rows.filter((row) => decisions[row.siren]?.accepted && decisions[row.siren]?.replaceExisting && row.existingState === 'different').length;
    const replacedText = conflictsReplaced > 0
      ? `, dont ${conflictsReplaced} ${conflictsReplaced > 1 ? 'remplacent un contact existant' : 'remplace un contact existant'}`
      : '';
    const managedText = `mettre à jour « Client en gestion » sur ${managedChanges} SCI`;
    let question = `Écrire ${counts.accepted} contact(s) SCI pour cette entreprise${replacedText}${managedChanges > 0 ? ` et ${managedText}` : ''} ?`;
    if (counts.accepted === 0) {
      question = `Aucun contact coché : ${managedText} pour cette entreprise ?`;
    }
    if (!window.confirm(question)) {return;}

    await onSave({ status: 'completed', sciDecisions: decisions });
    await onApply();
  };


  return (
    <div className="space-y-6">
      {hasBeenApplied && (
        <div className="rounded-lg border border-green-200 bg-green-50 p-4 text-sm text-green-800">
          Cet import a déjà été appliqué
          {appliedImportId && (
            <>
              {' '}
              (
              <Link className="font-semibold underline" to={ROUTES.ADMIN_IMPORT_DETAIL.replace(':importId', String(appliedImportId))}>
                import #
                {appliedImportId}
              </Link>
              )
            </>
          )}
          .
        </div>
      )}
      {!isLatestTestImport && (
        <div className="flex items-center gap-2 rounded-lg border border-orange-200 bg-orange-50 p-4 text-sm text-orange-800">
          <ExclamationTriangleIcon className="h-5 w-5 shrink-0" />
          Une analyse plus récente existe pour cette entreprise : vérifiez que vous travaillez sur le bon fichier.
        </div>
      )}

      <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
        {[
          { label: 'SCI dans le fichier', value: summary.total },
          { label: 'Rapprochement sûr', value: summary.sure },
          { label: 'Rapprochement probable', value: summary.probable },
          { label: 'Sans correspondance', value: counts.no_match },
          { label: 'Non importables', value: counts.not_importable },
        ].map((card) => (
          <div key={card.label} className="rounded-lg border border-gray-200 bg-gray-50 p-3">
            <p className="text-xs text-gray-500">{card.label}</p>
            <p className="text-2xl font-bold text-gray-800">{card.value ?? 0}</p>
          </div>
        ))}
      </div>

      <p className="text-sm text-gray-600">
        Seuls les dirigeants personnes physiques connus en base (ceux de la fiche SCI) peuvent être enregistrés comme contact.
        Le nom et le prénom viennent du dirigeant, le téléphone, l&apos;email et l&apos;adresse du fichier.
      </p>
      {managedChanges > 0 && (
        <p className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-800">
          {`La colonne « Client en gestion » mettra à jour ${managedChanges} SCI à la validation, sans revue : ce n'est pas une donnée personnelle.`}
        </p>
      )}

      <div className="flex flex-wrap items-center gap-2">
        {([
          ['match', `À valider (${counts.match})`],
          ['no_match', `Sans correspondance (${counts.no_match})`],
          ['not_importable', `Non importables (${counts.not_importable})`],
        ] as [Filter, string][]).map(([value, label]) => (
          <button
            key={value}
            type="button"
            onClick={() => setFilter(value)}
            className={`rounded-full px-4 py-1.5 text-sm font-medium ${filter === value ? 'bg-rgpd-blue-dark text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
          >
            {label}
          </button>
        ))}
        {filter === 'match' && (
          <select
            value={levelFilter}
            onChange={(event) => setLevelFilter(event.target.value as typeof levelFilter)}
            className="ml-2 rounded-md border border-gray-300 px-2 py-1 text-sm"
          >
            <option value="all">Tous les niveaux</option>
            <option value="sure">Sûrs</option>
            <option value="probable">Probables</option>
            <option value="conflict">Contact existant différent</option>
          </select>
        )}
        <div className="ml-auto flex flex-wrap gap-2">
          <button type="button" onClick={() => setAllSure(true)} className="rounded-md border border-gray-300 px-3 py-1.5 text-sm hover:bg-gray-50">
            Cocher tous les sûrs
          </button>
          <button type="button" onClick={uncheckAll} className="rounded-md border border-gray-300 px-3 py-1.5 text-sm hover:bg-gray-50">
            Tout décocher
          </button>
        </div>
      </div>

      <div className="overflow-x-auto rounded-lg border border-gray-200">
        <table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead className="bg-gray-50 text-left text-xs font-semibold text-gray-600 uppercase">
            <tr>
              {filter !== 'not_importable' && <th className="px-3 py-2">Importer</th>}
              <th className="px-3 py-2">SCI</th>
              <th className="px-3 py-2">Contact du fichier</th>
              {filter === 'not_importable' ? (
                <th className="px-3 py-2">Motif</th>
              ) : (
                <>
                  <th className="px-3 py-2">Rapprochement</th>
                  <th className="px-3 py-2">Dirigeant retenu</th>
                  <th className="px-3 py-2">Email retenu</th>
                  <th className="px-3 py-2">Contact existant</th>
                </>
              )}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {visibleRows.map((row) => {
              const decision = decisions[row.siren];
              const isManual = !!decision && decision.officerKey !== row.proposedOfficerKey;
              return (
                <tr key={`${row.line}-${row.siren}`} className={decision?.accepted ? 'bg-green-50/40' : ''}>
                  {filter !== 'not_importable' && (
                    <td className="px-3 py-2 align-top">
                      <input
                        type="checkbox"
                        className="h-4 w-4"
                        checked={!!decision?.accepted}
                        disabled={!decision?.officerKey}
                        onChange={(event) => updateDecision(row.siren, { accepted: event.target.checked })}
                        aria-label={`Importer le contact de ${row.denomination}`}
                      />
                    </td>
                  )}
                  <td className="px-3 py-2 align-top">
                    <p className="font-medium text-gray-800">{row.denomination || '—'}</p>
                    <p className="font-mono text-xs text-gray-500">
                      {row.siren}
                      {' · ligne '}
                      {row.line}
                    </p>
                    {row.managedChange && (
                      <p className="mt-1 text-xs font-medium text-emerald-700">{`Client en gestion → ${row.managed ? 'Oui' : 'Non'}`}</p>
                    )}
                  </td>
                  <td className="px-3 py-2 align-top text-gray-700">
                    <p>{[row.file.civilite, row.file.nom, row.file.prenom].filter(Boolean).join(' ') || '—'}</p>
                    {row.file.email && <p className="text-xs break-all text-gray-500">{row.file.email}</p>}
                    {row.file.telephone && <p className="text-xs text-gray-500">{row.file.telephone}</p>}
                  </td>
                  {filter === 'not_importable' ? (
                    <td className="px-3 py-2 align-top text-gray-700">{row.statusLabel}</td>
                  ) : (
                    <>
                      <td className="px-3 py-2 align-top">
                        <Badge size="small" mode={levelBadgeMode(row)} text={SCI_LEVEL_LABELS[row.level]} />
                        {row.reason && <p className="mt-1 max-w-56 text-xs text-gray-500">{row.reason}</p>}
                      </td>
                      <td className="px-3 py-2 align-top">
                        <select
                          value={decision?.officerKey ?? ''}
                          onChange={(event) => {
                            const officerKey = event.target.value || null;
                            updateDecision(row.siren, { officerKey, accepted: officerKey !== null });
                          }}
                          className="max-w-72 rounded-md border border-gray-300 px-2 py-1 text-sm"
                        >
                          <option value="">— Aucun —</option>
                          {row.officers.map((officer) => (
                            <option key={officer.key} value={officer.key}>
                              {`${formatOfficer(officer)} — ${officer.role}`}
                            </option>
                          ))}
                        </select>
                        {isManual && decision?.officerKey && <p className="mt-1 text-xs font-medium text-orange-700">Choix manuel</p>}
                      </td>
                      <td className="px-3 py-2 align-top">
                        {row.emails.length > 1 ? (
                          <select
                            value={decision?.email ?? ''}
                            onChange={(event) => updateDecision(row.siren, { email: event.target.value || null })}
                            className="max-w-64 rounded-md border border-gray-300 px-2 py-1 text-sm"
                          >
                            {row.emails.map((email) => <option key={email} value={email}>{email}</option>)}
                          </select>
                        ) : (
                          <span className="text-xs break-all text-gray-700">{row.emails[0] ?? '—'}</span>
                        )}
                      </td>
                      <td className="px-3 py-2 align-top text-xs text-gray-700">
                        {row.existing ? (
                          <>
                            <p>{formatContact(row.existing)}</p>
                            {row.existingState === 'identical' ? (
                              <p className="mt-1 text-green-700">Déjà à jour</p>
                            ) : (
                              <label className="mt-1 flex items-center gap-1 text-orange-700">
                                <input
                                  type="checkbox"
                                  checked={!!decision?.replaceExisting}
                                  onChange={(event) => updateDecision(row.siren, { replaceExisting: event.target.checked })}
                                />
                                Remplacer
                              </label>
                            )}
                          </>
                        ) : '—'}
                      </td>
                    </>
                  )}
                </tr>
              );
            })}
            {visibleRows.length === 0 && (
              <tr>
                <td colSpan={6} className="px-3 py-6 text-center text-gray-500">Aucune ligne.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="flex flex-wrap items-center justify-end gap-3 border-t border-gray-200 pt-4">
        <span className="text-sm text-gray-600">
          {counts.accepted}
          {' '}
          contact(s) cochés
        </span>
        <button
          type="button"
          onClick={handleSave}
          disabled={saving || applying}
          className="rounded-md border border-rgpd-blue-dark px-4 py-2 text-sm font-medium text-rgpd-blue-dark hover:bg-blue-50 disabled:opacity-50"
        >
          {saving ? 'Enregistrement…' : 'Enregistrer la revue'}
        </button>
        <button
          type="button"
          onClick={handleApply}
          disabled={saving || applying || hasBeenApplied || (counts.accepted === 0 && managedChanges === 0)}
          className="rounded-md bg-rgpd-blue-dark px-4 py-2 text-sm font-medium text-white hover:opacity-90 disabled:opacity-50"
        >
          {applying && 'Application…'}
          {!applying && (counts.accepted === 0 && managedChanges > 0 ? 'Valider « Client en gestion »' : 'Valider et écrire les contacts')}
        </button>
      </div>
    </div>
  );
};

export default SciImportReview;
